import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2, RotateCcw } from "lucide-react";

const PLACEHOLDERS = ["{first_name}", "{last_name}", "{company}", "{review_url}"];

export const ReviewSettingsForm = ({ isOwner, onSaved }) => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reviews/settings`).then(r => setS(r.data)).catch(() => toast.error("Failed to load settings"));
  }, []);

  const set = (k, v) => setS(prev => ({ ...prev, [k]: v }));
  const resetField = (k) => set(k, s.defaults[k]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { defaults, ...payload } = s;
      await axios.put(`${API}/reviews/settings`, payload);
      toast.success("Review settings saved");
      onSaved?.();
    } catch (err) {
      if (err.response?.status === 202) toast.info("Changes sent to owner for approval");
      else toast.error(err.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>;

  return (
    <div className="space-y-5" data-testid="review-settings-form">
      {!isOwner && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="review-settings-approval-note">
          Changes you make here are sent to the owner for approval before they take effect.
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Automatic Sending</CardTitle>
          <CardDescription>Send the review request automatically when a project is marked completed.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Auto-send on project completion</p>
              <p className="text-xs text-slate-500">Uses the message templates below, with QR code attached</p>
            </div>
            <Switch checked={!!s.review_requests_enabled} onCheckedChange={v => set("review_requests_enabled", v)} data-testid="auto-send-toggle" />
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-sm whitespace-nowrap">Send</Label>
            <Input type="number" min="0" className="w-20" value={s.review_auto_delay_days} onChange={e => set("review_auto_delay_days", e.target.value)} data-testid="auto-delay-input" />
            <span className="text-sm text-slate-600">day(s) after completion <span className="text-slate-400">(0 = same day)</span></span>
          </div>
          <div>
            <Label className="text-sm">Google Business Review URL</Label>
            <Input value={s.google_review_url || ""} onChange={e => set("google_review_url", e.target.value)} placeholder="https://g.page/r/.../review" data-testid="review-url-input" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Message Templates</CardTitle>
          <CardDescription>
            Placeholders: {PLACEHOLDERS.map(p => <code key={p} className="bg-slate-100 px-1 rounded mx-0.5 text-xs">{p}</code>)}
            — the 5-star button and QR code are always added below the email.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <TemplateField label="Text message (SMS)" value={s.review_sms_template} onChange={v => set("review_sms_template", v)} onReset={() => resetField("review_sms_template")} rows={6} testId="tpl-sms" />
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-sm">Email subject</Label>
              <ResetBtn onClick={() => resetField("review_email_subject")} testId="tpl-email-subject-reset" />
            </div>
            <Input value={s.review_email_subject} onChange={e => set("review_email_subject", e.target.value)} data-testid="tpl-email-subject" />
          </div>
          <TemplateField label="Email message" value={s.review_email_body} onChange={v => set("review_email_body", v)} onReset={() => resetField("review_email_body")} rows={14} testId="tpl-email-body" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Gentle Reminders</CardTitle>
          <CardDescription>Nudge customers who haven't been marked as reviewed. Each reminder includes an "I already left a review" link that stops further reminders.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="font-medium text-sm">Send reminders</p>
            <Switch checked={!!s.review_reminder_enabled} onCheckedChange={v => set("review_reminder_enabled", v)} data-testid="reminder-toggle" />
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
            <span>Every</span>
            <Input type="number" min="1" className="w-20" value={s.review_reminder_interval_days} onChange={e => set("review_reminder_interval_days", e.target.value)} data-testid="reminder-interval-input" />
            <span>days, up to</span>
            <Input type="number" min="0" className="w-20" value={s.review_reminder_max} onChange={e => set("review_reminder_max", e.target.value)} data-testid="reminder-max-input" />
            <span>reminders per customer</span>
          </div>
          <TemplateField label="Reminder text (sent by SMS, or email if no phone)" value={s.review_reminder_template} onChange={v => set("review_reminder_template", v)} onReset={() => resetField("review_reminder_template")} rows={5} testId="tpl-reminder" />
          <p className="text-xs text-slate-400">Extra placeholder: <code className="bg-slate-100 px-1 rounded">{"{reviewed_url}"}</code> — the "I already left a review" link.</p>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} data-testid="save-review-settings">
        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {isOwner ? "Save Review Settings" : "Submit Changes for Approval"}
      </Button>
    </div>
  );
};

const ResetBtn = ({ onClick, testId }) => (
  <button type="button" onClick={onClick} className="text-xs text-slate-400 hover:text-indigo-600 flex items-center gap-1" data-testid={testId}>
    <RotateCcw className="w-3 h-3" /> Reset to default
  </button>
);

const TemplateField = ({ label, value, onChange, onReset, rows, testId }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <Label className="text-sm">{label}</Label>
      <ResetBtn onClick={onReset} testId={`${testId}-reset`} />
    </div>
    <Textarea rows={rows} value={value} onChange={e => onChange(e.target.value)} className="text-sm" data-testid={testId} />
  </div>
);
